'use client';

import React, { useEffect, useRef } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { trackEvent } from '@/lib/analytics';

interface ServiceItem {
  number: string;
  category: string;
  title: string;
  subtitle: string;
  description: string;
  icon: string;
  features: string[];
}

export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { t } = useLanguage();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const particles: { x: number; y: number; vx: number; vy: number; size: number; opacity: number }[] = [];
    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        size: Math.random() * 1.5 + 0.5,
        opacity: Math.random() * 0.5 + 0.1,
      });
    }

    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(226, 183, 20, ${p.opacity})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-blackout">
      {/* Particle canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full opacity-60" />

      {/* Spotlight gradient */}
      <div className="absolute inset-0 bg-spotlight pointer-events-none" />

      {/* Grid lines */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(226,183,20,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(226,183,20,0.5) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />

      <div className="relative z-10 text-center px-4 sm:px-6 max-w-5xl mx-auto w-full">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 border border-gold/20 rounded-full px-3 sm:px-4 py-1.5 mb-6 sm:mb-8 bg-gold/5 backdrop-blur-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
          <span className="text-gold text-xs font-semibold uppercase tracking-widest">{t('hero.badge')}</span>
        </div>

        {/* Headline */}
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-hero font-black text-monitor leading-tight sm:leading-none tracking-tight mb-4 sm:mb-6">
          <span className="block">{t('hero.headline1')}</span>
          <span className="block text-transparent bg-clip-text bg-gold-gradient">
            {t('hero.headline2')}
          </span>
        </h1>

        <p className="text-monitor/50 text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed px-2 sm:px-0">
          {t('hero.description')}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 px-2 sm:px-0">
          <a
            href="#services"
            onClick={() => trackEvent('cta_click', { cta_id: 'hero_services', cta_text: 'explore_services', location: 'hero' })}
            className="w-full sm:w-auto gold-shimmer-btn text-blackout text-xs sm:text-sm font-black uppercase tracking-widest px-6 sm:px-8 py-3 sm:py-3.5 rounded-full transition-all hover:shadow-gold-glow"
          >
            {t('hero.cta_services')}
          </a>
          <a
            href="#contact"
            onClick={() => trackEvent('cta_click', { cta_id: 'hero_contact', cta_text: 'get_in_touch', location: 'hero' })}
            className="w-full sm:w-auto border border-white/10 text-monitor/70 hover:text-monitor hover:border-white/20 text-xs sm:text-sm font-semibold uppercase tracking-widest px-6 sm:px-8 py-3 sm:py-3.5 rounded-full transition-all duration-300"
          >
            {t('hero.cta_contact')}
          </a>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-blackout to-transparent pointer-events-none" />
    </section>
  );
}
